import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { getAllManagersSuperAdmin, updateManagerStatus } from "../../services/service";
import DataGrid from "../../atoms/DataTable/DataGrid";
import { message, Modal } from "antd";
import ListHeader from "../../molecules/ListHeader/ListHeader";

const AllManagers = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const rowsPerPageRef = useRef(rowsPerPage);

  useEffect(() => {
    rowsPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);

  const fetchManagers = useCallback(
    async (page = 1, limit) => {
      const effectiveLimit = limit ?? rowsPerPageRef.current;
      const statusParam =
        statusFilter === "active"
          ? "true"
          : statusFilter === "inactive"
            ? "false"
            : undefined;
      const searchParam = debouncedSearch?.trim() || undefined;

      setLoading(true);
      try {
        const response = await getAllManagersSuperAdmin(page, effectiveLimit, {
          search: searchParam,
          is_active: statusParam,
        });
        console.log("Managers Response:", response);
        if (response?.success) {
          setManagers(response.data || []);
          if (response.pagination) {
            const nextPageSize =
              response.pagination.itemsPerPage || effectiveLimit;
            setRowsPerPage(nextPageSize);
            setPagination({
              current: response.pagination.currentPage,
              pageSize: nextPageSize,
              total: response.pagination.totalItems,
            });
          } else {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: effectiveLimit,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching managers:", error);
      }
      setLoading(false);
    },
    [statusFilter, debouncedSearch]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchManagers(1, rowsPerPageRef.current);
  }, [fetchManagers]);

  const handlePageChange = (newPage, newPageSize) => {
    setRowsPerPage(newPageSize);
    setPagination((prev) => ({
      ...prev,
      current: newPage,
      pageSize: newPageSize,
    }));
    fetchManagers(newPage, newPageSize);
  };

  const handleSearchInputChange = (value = "") => {
    setSearchTerm(value);
  };

  const handleFilterChange = (values = {}) => {
    const nextStatus = values?.status || "all";
    setStatusFilter(nextStatus);
  };

  const handleStatusChange = async (managerId, newStatus) => {
    Modal.confirm({
      centered: true,
      title: `${newStatus ? 'Activate' : 'Deactivate'} Manager`,
      content: `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} this manager?`,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: {
        style: {
          backgroundColor: '#F97316',
          borderColor: '#F97316',
          color: 'white',
        },
      },
      onOk: async () => {
        try {
          const response = await updateManagerStatus(managerId, newStatus);
          if (response?.success) {
            message.success(`Manager ${newStatus ? 'activated' : 'deactivated'} successfully`);
            // Refresh the managers list
            fetchManagers(pagination.current, pagination.pageSize);
          } else {
            message.error(response?.message || "Failed to update manager status");
          }
        } catch (error) {
          console.error("Error updating manager status:", error);
          message.error("Failed to update manager status");
        }
      },
    });
  };

  // Transform managers data to match DataGrid format
  const transformedData = managers.map((manager) => ({
    id: manager.id,
    username: manager.username || manager.name || 'N/A',
    email: manager.email || 'N/A',
    phoneNo: manager.phoneNo || manager.phone || 'N/A',
    organization: manager.OrganizationUsers?.title || manager.organization?.name || manager.Organization?.name || 'N/A',
    is_active: manager.is_active !== undefined ? manager.is_active : true,
    status: manager.is_active ? 'Active' : 'Inactive',
    onStatusChange: handleStatusChange,
  }));

  return (
    <div className="row px-[5px] py-4">
      <div className="col-12">
        <div className="row d-flex align-items-end">
          <div className="col-12">
            <ListHeader
              mainHeading="All Managers"
              placeholder="Search by name, email, phone, or organization"
              handleSearchChange={handleFilterChange}
              linkBreadCrum="/super-admin/managers"
              blinkBreadCrumText="Managers"
              blinkBreadCrumText1="All Managers"
              searchShow={true}
              searchValue={searchTerm}
              handleProjectSearchChange={handleSearchInputChange}
              moduleType="super-admin-managers"
              showFilter={true}
              DropDown={false}
            />
          </div>
        </div>
      </div>

      <div className="w-full px-2 pt-4">
        <DataGrid
          index={12}
          loader={loading}
          data={transformedData}
          pagination={pagination}
          onPageChange={handlePageChange}
          getData={fetchManagers}
          hideActions={true}
        />
      </div>
    </div>
  );
};

export default AllManagers;

